import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Offcanvas,
  Form,
  Dropdown,
  Nav, // Import Nav for Tabs
  Tab, // Import Tab for Tabs
} from "react-bootstrap";
import {
  EllipsisIcon,
  Pen,
  Trash,
  X,
  DollarSign,
  Calendar,
} from "lucide-react";
import { getApiClient } from "../libs/axiosInstance";
import { getCurrencySymbol } from "../libs/countryData";
import Swal from "sweetalert2";
import CreatableSelect from "react-select/creatable";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { loadAuth } from "../libs/authMgmt";
import Wrapper from "./_Wrapper";
import Select from "react-select";

// New default categories for a business's expenses
const defaultExpenseCategories = new Set([
  "Rent",
  "Utilities (Electricity, Water, Gas)",
  "Office Supplies",
  "Marketing / Advertising",
  "Software Subscriptions",
  "Repairs & Maintenance",
  "Business Meals & Entertainment",
  "Travel Expenses",
  "Loan Interest / Bank Fees",
  "Taxes & Licenses (Non-Payroll)",
  "Insurance",
  "Delivery & Shipping Costs",
  "Telephone & Internet",
]);

// Helper to format currency
const formatCurrency = (amount) => {
  return `${getCurrencySymbol()}${parseFloat(amount || 0).toFixed(2)}`;
};

/**
 * =================================================================
 * 1. OTHER EXPENSES TAB (Simple Expense Model)
 * =================================================================
 */

// Initial state for the simple "Other Expense" form
const initialOtherExpenseState = {
  description: "",
  cost: 0, // Corrected from 'amount' to 'cost' to match the Mongoose model
  category: "",
  expenseDate: new Date().toISOString().substring(0, 10), // Default to today in YYYY-MM-DD format
};

function OtherExpensesTab({ otherExpenses, setOtherExpenses, loading, error }) {
  // CRUD and UI state
  const [handleAdd, setHandleAdd] = useState(false);
  const [handleState, setHandleState] = useState(""); // "add" or "edit"
  const [expenseId, setExpenseId] = useState(""); // ID of the expense being edited
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  // Expense form state
  const [handleFormSubmit, setHandleFormSubmit] = useState(
    initialOtherExpenseState
  );

  // Calculate options for the CreatableSelect (Expense Categories)
  const options = useMemo(() => {
    const allCats = [
      ...Array.from(defaultExpenseCategories),
      ...otherExpenses.map((expense) => expense.category),
    ];
    const unique = Array.from(new Set(allCats)).sort();
    return unique.map((cat) => ({ value: cat, label: cat }));
  }, [otherExpenses]);

  /**
   * Simple validation logic for expense form.
   */
  const expenseValidate = useCallback(() => {
    if (!handleFormSubmit.description.trim())
      return "Expense name is required.";
    if (handleFormSubmit.cost <= 0) return "Cost must be a positive amount.";
    if (!handleFormSubmit.category.trim())
      return "An expense category is required.";
    if (!handleFormSubmit.expenseDate) return "An expense date is required.";
    return null;
  }, [handleFormSubmit]);

  // --- Table Column Bodies ---

  const expenseFieldCost = (rowData) => {
    return (
      <span className="text-danger font-semibold">
        {formatCurrency(rowData.amount)}
      </span>
    );
  };

  const expenseFieldDate = (rowData) => {
    // We use expenseDate field from the model now
    const date = rowData.expenseDate
      ? new Date(rowData.expenseDate)
      : new Date();
    return date.toLocaleDateString();
  };

  // --- CRUD Operations ---

  /**
   * Handles expense creation.
   */
  async function submitExpenseForm(e) {
    e.preventDefault();

    const validationError = expenseValidate();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await getApiClient().post(
        "/api/expenses",
        {
          description: handleFormSubmit.description,
          cost: handleFormSubmit.cost,
          category: handleFormSubmit.category,
          expenseDate: handleFormSubmit.expenseDate,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      setOtherExpenses((prevExpenses) => [response.data, ...prevExpenses]);
      setHandleAdd(false);
      Swal.fire("Success", "Expense added successfully.", "success");
      setHandleFormSubmit(initialOtherExpenseState);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save expense.", "error");
    }
    setIsSubmitting(false);
  }

  /**
   * Handles expense update.
   */
  async function updateSubmitExpenseForm(e) {
    e.preventDefault();
    const validationError = expenseValidate();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await getApiClient().put(
        `/api/expenses?id=${expenseId}`,
        {
          description: handleFormSubmit.description,
          cost: handleFormSubmit.cost,
          category: handleFormSubmit.category,
          expenseDate: handleFormSubmit.expenseDate,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      setOtherExpenses((prevExpenses) =>
        prevExpenses.map((expense) =>
          expense._id === expenseId ? response.data : expense
        )
      );
      setHandleAdd(false);
      Swal.fire("Success", "Expense updated successfully.", "success");
      setHandleFormSubmit(initialOtherExpenseState);
      setExpenseId("");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update expense.", "error");
    }
    setIsSubmitting(false);
  }

  /**
   * Handles expense deletion.
   */
  const handleDeleteExpense = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await getApiClient().delete(`/api/expenses?id=${id}`, {
            headers: { "Content-Type": "application/json" },
          });
          setOtherExpenses((prevExpenses) =>
            prevExpenses.filter((e) => e._id !== id)
          );
          Swal.fire("Deleted!", "Your expense has been deleted.", "success");
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", "Failed to delete expense.", "error");
        }
      }
    });
  };

  const handleEditClick = async (rowData) => {
    setHandleAdd(true);
    setHandleState("edit");
    try {
      // Fetch single expense data to populate form
      const res = await getApiClient().get(`/api/expenses?id=${rowData._id}`, {
        headers: { "Content-Type": "application/json" },
      });
      setHandleFormSubmit({
        description: res.data.description,
        cost: res.data.cost,
        category: res.data.category,
        // Format date string for the input[type=date]
        expenseDate: new Date(res.data.expenseDate)
          .toISOString()
          .substring(0, 10),
      });
      setExpenseId(rowData._id);
    } catch (e) {
      Swal.fire("Error", "Could not load expense details.", "error");
      setHandleAdd(false);
    }
  };

  return (
    <>
      <Row className="mb-4">
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center"
        >
          <h2 className="text-xl font-bold">Other Business Expenses</h2>
          <Button
            variant="primary"
            onClick={() => {
              setHandleFormSubmit(initialOtherExpenseState);
              setHandleAdd(true);
              setHandleState("add");
            }}
          >
            <DollarSign size={18} className="me-1" /> Add Other Expense
          </Button>
        </Col>
      </Row>

      {loading && <p>Loading other expenses...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {!loading && !error && (
        <>
          {/* Global Search Input */}
          <div className="mb-3">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={(e) => setGlobalFilterValue(e.target.value)}
                placeholder="Search description, category..."
                className="form-control"
              />
            </IconField>
          </div>

          {/* Expense DataTable */}
          <DataTable
            className="inventory_table rounded-lg shadow-sm"
            value={otherExpenses}
            paginator
            rows={10}
            tableStyle={{ minWidth: "50rem" }}
            globalFilter={globalFilterValue}
            globalFilterFields={["description", "category", "cost"]}
            emptyMessage="No other expenses found."
          >
            <Column field="description" header="Description" sortable></Column>
            <Column
              field="cost"
              header="Cost"
              sortable
              className="item_cost"
              body={expenseFieldCost}
            ></Column>
            <Column field="category" header="Category" sortable></Column>
            <Column
              field="expenseDate"
              header="Date Incurred"
              sortable
              body={expenseFieldDate}
            ></Column>
            <Column
              header="Action"
              body={(rowData) => (
                <div>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="light"
                      id={`dropdown-${rowData._id}`}
                      className="custom_dropdown_toggle"
                      style={{ background: "none", border: "none", padding: 0 }}
                    >
                      <EllipsisIcon size={18} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleEditClick(rowData)}>
                        <Pen className="me-2" size={16} /> Edit Expense
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => handleDeleteExpense(rowData._id)}
                      >
                        <Trash className="me-2" size={16} /> Delete Expense
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
            ></Column>
          </DataTable>
        </>
      )}

      {/* Other Expense Offcanvas Form */}
      <Offcanvas
        show={handleAdd}
        onHide={() => setHandleAdd(false)}
        placement="end"
        style={{ width: "400px" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="font-bold">
            {handleState === "add" ? "Add Other Expense" : "Edit Other Expense"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form
            onSubmit={
              handleState === "add"
                ? submitExpenseForm
                : updateSubmitExpenseForm
            }
          >
            {/* Expense Date */}
            <Form.Group className="mb-3" controlId="formExpenseDate">
              <Form.Label className="font-medium">Date Incurred</Form.Label>
              <InputText
                id="formExpenseDate"
                type="date"
                value={handleFormSubmit.expenseDate}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    expenseDate: e.target.value,
                  })
                }
                className="form-control"
                required
              />
            </Form.Group>

            {/* Expense Name (Description) */}
            <Form.Group className="mb-3" controlId="formExpenseName">
              <Form.Label className="font-medium">Description</Form.Label>
              <InputText
                id="formExpenseName"
                value={handleFormSubmit.description}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    description: e.target.value,
                  })
                }
                className="form-control"
                required
              />
            </Form.Group>

            {/* Expense Cost */}
            <Form.Group className="mb-3" controlId="formExpenseCost">
              <Form.Label className="font-medium">
                Cost ({getCurrencySymbol()})
              </Form.Label>
              <IconField iconPosition="left">
                <InputIcon className="pi pi-dollar" />
                <InputText
                  id="formExpenseCost"
                  type="number"
                  value={handleFormSubmit.cost}
                  onChange={(e) =>
                    setHandleFormSubmit({
                      ...handleFormSubmit,
                      cost: parseFloat(e.target.value) || 0,
                    })
                  }
                  min="0.01"
                  step="0.01"
                  className="form-control"
                  required
                />
              </IconField>
            </Form.Group>

            {/* Category Select */}
            <Form.Group className="mb-3" controlId="formExpenseCategory">
              <Form.Label className="font-medium">Category</Form.Label>
              <CreatableSelect
                isClearable
                options={options}
                value={
                  handleFormSubmit.category
                    ? {
                        value: handleFormSubmit.category,
                        label: handleFormSubmit.category,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    category: selectedOption ? selectedOption.value : "",
                  });
                }}
                onCreateOption={(inputValue) => {
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    category: inputValue,
                  });
                }}
                placeholder="Select or create a category..."
                required
              />
            </Form.Group>

            {/* Submission Button */}
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              className="mt-3 w-100"
            >
              {isSubmitting
                ? "Submitting..."
                : handleState === "add"
                ? "Add Expense"
                : "Save Changes"}
            </Button>
            <Button
              variant="light"
              className="mt-2 w-100"
              onClick={() => setHandleAdd(false)}
            >
              <X size={16} /> Cancel
            </Button>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

/**
 * =================================================================
 * 2. PAYROLL EXPENSES TAB (Detailed Payroll Model)
 * =================================================================
 */

// Initial state for the detailed "Payroll Expense" form
const initialPayrollExpenseState = {
  title: "",
  payPeriodStart: new Date().toISOString().substring(0, 10),
  payPeriodEnd: new Date().toISOString().substring(0, 10),
  grossWages: 0,
  netPayDistributed: 0,
  employerTaxesCost: 0,
  benefitsCost: 0,
  notes: "",
  status: "Completed",
  recipients: [], // array of user _ids
};

function PayrollExpensesTab({
  payrollExpenses,
  setPayrollExpenses,
  loading,
  error,
}) {
  // CRUD and UI state
  const [handleAdd, setHandleAdd] = useState(false);
  const [handleState, setHandleState] = useState(""); // "add" or "edit"
  const [expenseId, setExpenseId] = useState(""); // ID of the expense being edited
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  // Expense form state
  const [handleFormSubmit, setHandleFormSubmit] = useState(
    initialPayrollExpenseState
  );
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Load users when opening the form
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await getApiClient().get(
          "/api/users/payroll-users?active=1"
        );
        setUserOptions(
          (res.data || []).map((u) => ({
            value: u._id,
            label: u.name ? `${u.name} (${u.email})` : u.email,
          }))
        );
      } catch (e) {
        console.error("Failed to load users", e);
      } finally {
        setLoadingUsers(false);
      }
    };
    if (handleAdd) loadUsers();
  }, [handleAdd]);
  /**
   * Validation logic for payroll form.
   */
  const payrollValidate = useCallback(() => {
    const data = handleFormSubmit;
    if (!data.title.trim()) return "Title is required.";
    if (!data.payPeriodStart || !data.payPeriodEnd)
      return "Both pay period dates are required.";
    if (new Date(data.payPeriodStart) >= new Date(data.payPeriodEnd))
      return "Start date must be before end date.";
    if (data.grossWages <= 0) return "Gross Wages must be a positive amount.";

    // Calculate total cost and check against the stored value
    const calculatedTotal =
      data.grossWages + data.employerTaxesCost + data.benefitsCost || 0;

    // We update the calculated total cost based on the form inputs before submission
    // and rely on the server to handle the final calculation/validation if needed,
    // but here we check for data integrity issues.
    if (calculatedTotal < data.netPayDistributed)
      return "Total Employer Cost cannot be less than Net Pay Distributed.";

    return null;
  }, [handleFormSubmit]);

  // --- Table Column Bodies ---

  const payrollFieldCost = (field) => (rowData) => {
    return (
      <span className="font-semibold">{formatCurrency(rowData[field])}</span>
    );
  };

  const payrollFieldPeriod = (rowData) => {
    const start = new Date(rowData.payPeriodStart).toLocaleDateString();
    const end = new Date(rowData.payPeriodEnd).toLocaleDateString();
    return `${start} - ${end}`;
  };

  // --- CRUD Operations ---

  /**
   * Handles payroll expense creation.
   */
  async function submitPayrollForm(e) {
    e.preventDefault();

    const validationError = payrollValidate();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }

    // Calculate final total cost before submission
    const totalPayrollCost =
      (handleFormSubmit.grossWages || 0) +
      (handleFormSubmit.employerTaxesCost || 0) +
      (handleFormSubmit.benefitsCost || 0);

    setIsSubmitting(true);
    try {
      const payload = {
        ...handleFormSubmit,
        totalPayrollCost: totalPayrollCost,
        recipients: handleFormSubmit.recipients || [],
      };

      const response = await getApiClient().post(
        "/api/payroll", // New API endpoint
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      setPayrollExpenses((prevExpenses) => [response.data, ...prevExpenses]);
      setHandleAdd(false);
      Swal.fire("Success", "Payroll expense added successfully.", "success");
      setHandleFormSubmit(initialPayrollExpenseState);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        "Failed to save payroll expense. Check your input values.",
        "error"
      );
    }
    setIsSubmitting(false);
  }

  /**
   * Handles payroll expense update.
   */
  async function updateSubmitPayrollForm(e) {
    e.preventDefault();
    const validationError = payrollValidate();
    if (validationError) {
      Swal.fire("Validation Error", validationError, "error");
      return;
    }

    const totalPayrollCost =
      (handleFormSubmit.grossWages || 0) +
      (handleFormSubmit.employerTaxesCost || 0) +
      (handleFormSubmit.benefitsCost || 0);

    setIsSubmitting(true);
    try {
      const payload = {
        ...handleFormSubmit,
        totalPayrollCost: totalPayrollCost,
        recipients: handleFormSubmit.recipients || [],
      };

      const response = await getApiClient().put(
        `/api/payroll?id=${expenseId}`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      setPayrollExpenses((prevExpenses) =>
        prevExpenses.map((expense) =>
          expense._id === expenseId ? response.data : expense
        )
      );
      setHandleAdd(false);
      Swal.fire("Success", "Payroll expense updated successfully.", "success");
      setHandleFormSubmit(initialPayrollExpenseState);
      setExpenseId("");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update payroll expense.", "error");
    }
    setIsSubmitting(false);
  }

  /**
   * Handles payroll expense deletion.
   */
  const handleDeletePayrollExpense = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await getApiClient().delete(`/api/payroll?id=${id}`, {
            headers: { "Content-Type": "application/json" },
          });
          setPayrollExpenses((prevExpenses) =>
            prevExpenses.filter((e) => e._id !== id)
          );
          Swal.fire("Deleted!", "Payroll expense deleted.", "success");
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", "Failed to delete payroll expense.", "error");
        }
      }
    });
  };

  const handleEditClick = async (rowData) => {
    setHandleAdd(true);
    setHandleState("edit");
    try {
      // Fetch single expense data to populate form
      const res = await getApiClient().get(`/api/payroll?id=${rowData._id}`);
      setHandleFormSubmit({
        title: res.data.title,
        grossWages: res.data.grossWages,
        netPayDistributed: res.data.netPayDistributed,
        employerTaxesCost: res.data.employerTaxesCost,
        benefitsCost: res.data.benefitsCost,
        notes: res.data.notes || "",
        status: res.data.status || "Completed",
        // Format date strings for the input[type=date]
        payPeriodStart: new Date(res.data.payPeriodStart)
          .toISOString()
          .substring(0, 10),
        payPeriodEnd: new Date(res.data.payPeriodEnd)
          .toISOString()
          .substring(0, 10),
        recipients: (res.data.recipients || []).map((r) => r._id || r), // populated or raw ids
      });
      setExpenseId(rowData._id);
    } catch (e) {
      Swal.fire("Error", "Could not load payroll expense details.", "error");
      setHandleAdd(false);
    }
  };

  return (
    <>
      <Row className="mb-4">
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center"
        >
          <h2 className="text-xl font-bold">Aggregated Payroll Expenses</h2>
          <Button
            variant="primary"
            onClick={() => {
              setHandleFormSubmit(initialPayrollExpenseState);
              setHandleAdd(true);
              setHandleState("add");
            }}
          >
            <Calendar size={18} className="me-1" /> Add Payroll Entry
          </Button>
        </Col>
      </Row>

      {loading && <p>Loading payroll entries...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
      {!loading && !error && (
        <>
          {/* Global Search Input */}
          <div className="mb-3">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={globalFilterValue}
                onChange={(e) => setGlobalFilterValue(e.target.value)}
                placeholder="Search payroll titles..."
                className="form-control"
              />
            </IconField>
          </div>

          {/* Payroll DataTable */}
          <DataTable
            className="inventory_table rounded-lg shadow-sm"
            value={payrollExpenses}
            paginator
            rows={10}
            tableStyle={{ minWidth: "50rem" }}
            globalFilter={globalFilterValue}
            globalFilterFields={["title", "totalPayrollCost", "status"]}
            emptyMessage="No payroll entries found."
          >
            <Column field="title" header="Title" sortable></Column>
            <Column
              field="payPeriodStart"
              header="Pay Period"
              sortable
              body={payrollFieldPeriod}
            ></Column>
            <Column
              field="grossWages"
              header="Gross Wages"
              sortable
              body={payrollFieldCost("grossWages")}
            ></Column>
            <Column
              field="employerTaxesCost"
              header="Employer Taxes"
              sortable
              body={payrollFieldCost("employerTaxesCost")}
            ></Column>
            <Column
              field="totalPayrollCost"
              header="Total Cost"
              sortable
              body={payrollFieldCost("totalPayrollCost")}
            ></Column>
            {/* <Column field="status" header="Status" sortable></Column> */}

            <Column
              header="Recipients"
              body={(row) => row.recipients?.length || 0}
            ></Column>
            <Column
              header="Action"
              body={(rowData) => (
                <div>
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="light"
                      id={`dropdown-payroll-${rowData._id}`}
                      className="custom_dropdown_toggle"
                      style={{ background: "none", border: "none", padding: 0 }}
                    >
                      <EllipsisIcon size={18} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleEditClick(rowData)}>
                        <Pen className="me-2" size={16} /> Edit Entry
                      </Dropdown.Item>
                      <Dropdown.Item
                        className="text-danger"
                        onClick={() => handleDeletePayrollExpense(rowData._id)}
                      >
                        <Trash className="me-2" size={16} /> Delete Entry
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              )}
            ></Column>
          </DataTable>
        </>
      )}

      {/* Payroll Expense Offcanvas Form */}
      <Offcanvas
        show={handleAdd}
        onHide={() => setHandleAdd(false)}
        placement="end"
        style={{ width: "450px" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="font-bold">
            {handleState === "add" ? "Add Payroll Entry" : "Edit Payroll Entry"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form
            onSubmit={
              handleState === "add"
                ? submitPayrollForm
                : updateSubmitPayrollForm
            }
          >
            <Form.Group className="mb-3" controlId="formPayrollTitle">
              <Form.Label className="font-medium">Entry Title</Form.Label>
              <InputText
                id="formPayrollTitle"
                value={handleFormSubmit.title}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    title: e.target.value,
                  })
                }
                className="form-control"
                required
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="formPeriodStart">
                  <Form.Label className="font-medium">Period Start</Form.Label>
                  <InputText
                    type="date"
                    value={handleFormSubmit.payPeriodStart}
                    onChange={(e) =>
                      setHandleFormSubmit({
                        ...handleFormSubmit,
                        payPeriodStart: e.target.value,
                      })
                    }
                    className="form-control"
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3" controlId="formPeriodEnd">
                  <Form.Label className="font-medium">Period End</Form.Label>
                  <InputText
                    type="date"
                    value={handleFormSubmit.payPeriodEnd}
                    onChange={(e) =>
                      setHandleFormSubmit({
                        ...handleFormSubmit,
                        payPeriodEnd: e.target.value,
                      })
                    }
                    className="form-control"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-3 mb-3 font-semibold text-gray-700">
              Cost Components ({getCurrencySymbol()})
            </h5>

            {/* Gross Wages */}
            <Form.Group className="mb-3" controlId="formGrossWages">
              <Form.Label className="font-medium">
                Total Gross Wages (Pre-tax)
              </Form.Label>
              <InputText
                type="number"
                value={handleFormSubmit.grossWages}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    grossWages: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
                className="form-control"
                required
              />
            </Form.Group>

            {/* Net Pay Distributed */}
            <Form.Group className="mb-3" controlId="formNetPay">
              <Form.Label className="font-medium">
                Net Pay Distributed (Cash Outflow)
              </Form.Label>
              <InputText
                type="number"
                value={handleFormSubmit.netPayDistributed}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    netPayDistributed: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
                className="form-control"
                required
              />
            </Form.Group>

            {/* Employer Taxes */}
            <Form.Group className="mb-3" controlId="formEmployerTaxes">
              <Form.Label className="font-medium">
                Employer Taxes & Contributions
              </Form.Label>
              <InputText
                type="number"
                value={handleFormSubmit.employerTaxesCost}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    employerTaxesCost: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
                className="form-control"
              />
            </Form.Group>

            {/* Benefits Cost */}
            <Form.Group className="mb-3" controlId="formBenefitsCost">
              <Form.Label className="font-medium">
                Employer Benefits Cost (Insurance, 401k Match, etc.)
              </Form.Label>
              <InputText
                type="number"
                value={handleFormSubmit.benefitsCost}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    benefitsCost: parseFloat(e.target.value) || 0,
                  })
                }
                min="0"
                step="0.01"
                className="form-control"
              />
            </Form.Group>
            {/* Pay Recipients */}
            <Form.Group className="mb-3" controlId="formPayrollRecipients">
              <Form.Label className="font-medium">
                Pay Recipients (Users)
              </Form.Label>
              <Select
                isMulti
                isLoading={loadingUsers}
                options={userOptions}
                value={userOptions.filter((o) =>
                  (handleFormSubmit.recipients || []).includes(o.value)
                )}
                onChange={(vals) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    recipients: (vals || []).map((v) => v.value),
                  })
                }
                placeholder="Select users to receive pay..."
                classNamePrefix="react-select"
              />
              <Form.Text className="text-muted">
                This links the payroll entry to selected users.
              </Form.Text>
            </Form.Group>

            {/* Notes and Status (Optional) */}
            <Form.Group className="mb-3" controlId="formPayrollNotes">
              <Form.Label className="font-medium">Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={handleFormSubmit.notes}
                onChange={(e) =>
                  setHandleFormSubmit({
                    ...handleFormSubmit,
                    notes: e.target.value,
                  })
                }
              />
            </Form.Group>

            {/* Submission Button */}
            <Button
              variant="success"
              type="submit"
              disabled={isSubmitting}
              className="mt-3 w-100"
            >
              {isSubmitting
                ? "Submitting..."
                : handleState === "add"
                ? "Add Payroll Entry"
                : "Save Changes"}
            </Button>
            <Button
              variant="light"
              className="mt-2 w-100"
              onClick={() => setHandleAdd(false)}
            >
              <X size={16} /> Cancel
            </Button>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

/**
 * =================================================================
 * 3. MAIN EXPENSE MANAGEMENT COMPONENT (Tab Wrapper)
 * =================================================================
 */

export default function ExpenseManagement() {
  window.document.title = `Expenses - ${
    loadAuth("company")?.description ?? "NEST AI"
  }`;

  const [activeTab, setActiveTab] = useState("payroll"); // 'other' or 'payroll'

  // State for Other Expenses
  const [otherExpenses, setOtherExpenses] = useState([]);
  const [loadingOther, setLoadingOther] = useState(true);
  const [errorOther, setErrorOther] = useState("");

  // State for Payroll Expenses
  const [payrollExpenses, setPayrollExpenses] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(true);
  const [errorPayroll, setErrorPayroll] = useState("");

  // --- Data Fetching Effects ---

  // Effect to fetch Other Expenses
  useEffect(() => {
    const fetchOtherExpenses = async () => {
      setLoadingOther(true);
      try {
        const response = await getApiClient().get("/api/expenses");
        setOtherExpenses(response.data);
      } catch (err) {
        console.error("Error fetching other expenses:", err);
        setErrorOther("Failed to fetch other expenses.");
      } finally {
        setLoadingOther(false);
      }
    };
    if (activeTab === "other") {
      fetchOtherExpenses();
    }
  }, [activeTab]);

  // Effect to fetch Payroll Expenses
  useEffect(() => {
    const fetchPayrollExpenses = async () => {
      setLoadingPayroll(true);
      try {
        const response = await getApiClient().get("/api/payroll");
        setPayrollExpenses(response.data);
      } catch (err) {
        console.error("Error fetching payroll expenses:", err);
        setErrorPayroll("Failed to fetch payroll expenses.");
      } finally {
        setLoadingPayroll(false);
      }
    };
    if (activeTab === "payroll") {
      fetchPayrollExpenses();
    }
  }, [activeTab]);

  return (
    <Wrapper className="">
      <div
        className="expense-management-container rounded-lg inventory_cont"
        style={{
          padding: "20px",
          minHeight: "80vh",
          backgroundColor: "transparent",
        }}
      >
        <Container fluid>
          <Row className="mb-4">
            <Col>
              <h1 className="section_title font-extrabold text-3xl mb-0">
                Business Expense Center
              </h1>
              <p className="text-gray-500">
                Manage all operating and labor costs.
              </p>
            </Col>
          </Row>

          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Row>
              <Col xs={12}>
                <Nav
                  variant="tabs"
                  className="mb-4 border-b-2 border-gray-200"
                  style={{
                    backgroundColor: "#fff",
                  }}
                >
                  <Nav.Item>
                    <Nav.Link
                      eventKey="payroll"
                      className="font-semibold text-lg cursor-pointer"
                    >
                      Payroll Expenses
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="other"
                      className="font-semibold text-lg cursor-pointer"
                    >
                      Other Expenses
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <Tab.Content>
                  <Tab.Pane eventKey="payroll">
                    <PayrollExpensesTab
                      payrollExpenses={payrollExpenses}
                      setPayrollExpenses={setPayrollExpenses}
                      loading={loadingPayroll}
                      error={errorPayroll}
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey="other">
                    <OtherExpensesTab
                      otherExpenses={otherExpenses}
                      setOtherExpenses={setOtherExpenses}
                      loading={loadingOther}
                      error={errorOther}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      </div>
    </Wrapper>
  );
}
